import { useEffect, useState } from 'react'

// 像素貓咪 4 格動畫：坐著 → 舉爪 → 玩毛線球 → 舉爪
// 每格 32×32，以 <rect> 疊成

const P = 2 // 一個像素單位 = 2px

type Frame = { x: number; y: number; color: string }[]

// 共用色票
const C = {
  body:  'var(--color-navy)',
  ear:   'var(--color-navy)',
  face:  '#f5e6c8',
  eye:   'var(--color-navy)',
  nose:  'var(--color-coral)',
  yarn:  'var(--color-coral)',
  yarn2: 'var(--color-sun)',
  paw:   '#f5e6c8',
  tail:  'var(--color-navy)',
  shine: '#ffffff',
}

// 一格像素 helper：轉成 rect props
function px(x: number, y: number, color: string) {
  return { x: x * P, y: y * P, color }
}

// 貓咪 Frame 0 — 坐姿，看前方
const FRAME_SIT: Frame = [
  // 耳朵
  px(5,2,C.ear), px(6,1,C.ear), px(9,1,C.ear), px(10,2,C.ear),
  // 頭
  ...([4,5,6,7,8,9,10,11].map(x => px(x,3,C.body))),
  px(4,4,C.body), px(11,4,C.body),
  // 臉
  px(5,4,C.face),px(6,4,C.face),px(7,4,C.face),px(8,4,C.face),px(9,4,C.face),px(10,4,C.face),
  px(5,5,C.face),px(6,5,C.face),px(7,5,C.face),px(8,5,C.face),px(9,5,C.face),px(10,5,C.face),
  px(5,6,C.face),px(6,6,C.face),px(7,6,C.face),px(8,6,C.face),px(9,6,C.face),px(10,6,C.face),
  // 眼睛
  px(6,4,C.eye), px(9,4,C.eye),
  // 鼻子
  px(7,6,C.nose), px(8,6,C.nose),
  // 鬍鬚 (shine)
  px(3,5,C.body), px(12,5,C.body),
  // 身體
  ...([5,6,7,8,9,10].map(x => px(x,7,C.body))),
  ...([4,5,6,7,8,9,10,11].map(x => px(x,8,C.body))),
  ...([4,5,6,7,8,9,10,11].map(x => px(x,9,C.body))),
  ...([5,6,7,8,9,10].map(x => px(x,10,C.body))),
  // 前爪
  px(5,11,C.body), px(6,11,C.body), px(9,11,C.body), px(10,11,C.body),
  px(5,12,C.paw),  px(6,12,C.paw),  px(9,12,C.paw),  px(10,12,C.paw),
  // 尾巴
  px(11,10,C.tail), px(12,9,C.tail), px(12,8,C.tail), px(13,7,C.tail),
]

// 貓咪 Frame 1 — 舉右爪
const FRAME_RAISE: Frame = [
  // 耳朵
  px(5,2,C.ear), px(6,1,C.ear), px(9,1,C.ear), px(10,2,C.ear),
  // 頭
  ...([4,5,6,7,8,9,10,11].map(x => px(x,3,C.body))),
  px(4,4,C.body), px(11,4,C.body),
  // 臉
  px(5,4,C.face),px(6,4,C.face),px(7,4,C.face),px(8,4,C.face),px(9,4,C.face),px(10,4,C.face),
  px(5,5,C.face),px(6,5,C.face),px(7,5,C.face),px(8,5,C.face),px(9,5,C.face),px(10,5,C.face),
  px(5,6,C.face),px(6,6,C.face),px(7,6,C.face),px(8,6,C.face),px(9,6,C.face),px(10,6,C.face),
  px(6,4,C.eye), px(9,4,C.eye),
  px(7,6,C.nose), px(8,6,C.nose),
  px(3,5,C.body), px(12,5,C.body),
  // 身體
  ...([5,6,7,8,9,10].map(x => px(x,7,C.body))),
  ...([4,5,6,7,8,9,10,11].map(x => px(x,8,C.body))),
  ...([4,5,6,7,8,9,10,11].map(x => px(x,9,C.body))),
  ...([5,6,7,8,9,10].map(x => px(x,10,C.body))),
  // 前爪左 + 右爪舉起
  px(5,11,C.body), px(6,11,C.body), px(5,12,C.paw), px(6,12,C.paw),
  px(10,8,C.body), px(11,7,C.body), px(11,6,C.body), px(10,5,C.paw), // 舉爪
  // 尾巴
  px(11,10,C.tail), px(12,9,C.tail), px(12,8,C.tail),
]

// 貓咪 Frame 2 — 玩毛線球（低頭）
const FRAME_PLAY: Frame = [
  // 耳朵
  px(5,3,C.ear), px(6,2,C.ear), px(9,2,C.ear), px(10,3,C.ear),
  // 頭（低頭版，往下移一格）
  ...([4,5,6,7,8,9,10,11].map(x => px(x,4,C.body))),
  px(4,5,C.body), px(11,5,C.body),
  // 臉（低頭）
  px(5,5,C.face),px(6,5,C.face),px(7,5,C.face),px(8,5,C.face),px(9,5,C.face),px(10,5,C.face),
  px(5,6,C.face),px(6,6,C.face),px(7,6,C.face),px(8,6,C.face),px(9,6,C.face),px(10,6,C.face),
  px(5,7,C.face),px(6,7,C.face),px(7,7,C.face),px(8,7,C.face),px(9,7,C.face),px(10,7,C.face),
  px(6,5,C.eye), px(9,5,C.eye),
  px(7,7,C.nose), px(8,7,C.nose),
  // 身體
  ...([5,6,7,8,9,10].map(x => px(x,8,C.body))),
  ...([4,5,6,7,8,9,10,11].map(x => px(x,9,C.body))),
  ...([4,5,6,7,8,9,10,11].map(x => px(x,10,C.body))),
  // 爪子按著毛線球
  px(5,11,C.body), px(6,11,C.paw),
  px(9,11,C.body), px(10,11,C.paw),
  // 毛線球
  px(6,12,C.yarn), px(7,12,C.yarn), px(8,12,C.yarn2), px(9,12,C.yarn),
  px(7,13,C.yarn2), px(8,13,C.yarn),
  // 尾巴（搖擺）
  px(11,9,C.tail), px(12,8,C.tail), px(13,7,C.tail), px(14,7,C.tail),
]

// 貓咪 Frame 3 — 舉左爪（鏡像）
const FRAME_RAISE_L: Frame = [
  px(5,2,C.ear), px(6,1,C.ear), px(9,1,C.ear), px(10,2,C.ear),
  ...([4,5,6,7,8,9,10,11].map(x => px(x,3,C.body))),
  px(4,4,C.body), px(11,4,C.body),
  px(5,4,C.face),px(6,4,C.face),px(7,4,C.face),px(8,4,C.face),px(9,4,C.face),px(10,4,C.face),
  px(5,5,C.face),px(6,5,C.face),px(7,5,C.face),px(8,5,C.face),px(9,5,C.face),px(10,5,C.face),
  px(5,6,C.face),px(6,6,C.face),px(7,6,C.face),px(8,6,C.face),px(9,6,C.face),px(10,6,C.face),
  px(6,4,C.eye), px(9,4,C.eye),
  px(7,6,C.nose), px(8,6,C.nose),
  px(3,5,C.body), px(12,5,C.body),
  ...([5,6,7,8,9,10].map(x => px(x,7,C.body))),
  ...([4,5,6,7,8,9,10,11].map(x => px(x,8,C.body))),
  ...([4,5,6,7,8,9,10,11].map(x => px(x,9,C.body))),
  ...([5,6,7,8,9,10].map(x => px(x,10,C.body))),
  px(9,11,C.body), px(10,11,C.body), px(9,12,C.paw), px(10,12,C.paw),
  px(5,8,C.body), px(4,7,C.body), px(4,6,C.body), px(5,5,C.paw), // 舉爪
  px(11,10,C.tail), px(12,9,C.tail), px(12,8,C.tail),
]

const FRAMES = [FRAME_SIT, FRAME_RAISE, FRAME_PLAY, FRAME_RAISE_L]
const INTERVAL_MS = 200

interface PixelCatLoadingProps {
  size?: number
  label?: string
}

export function PixelCatLoading({ size = 64, label }: PixelCatLoadingProps) {
  const [frame, setFrame] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setFrame(f => (f + 1) % FRAMES.length), INTERVAL_MS)
    return () => clearInterval(id)
  }, [])

  const scale = size / (16 * P)  // 畫布 16 格 × 2px = 32px，縮放到 size
  const pixels = FRAMES[frame]

  return (
    <div className="flex flex-col items-center gap-3">
      <svg
        viewBox={`0 0 ${16 * P} ${16 * P}`}
        width={size}
        height={size}
        shapeRendering="crispEdges"
        aria-label="載入中"
      >
        {pixels.map((p, i) => (
          <rect key={i} x={p.x} y={p.y} width={P} height={P} fill={p.color} />
        ))}
      </svg>
      {label && (
        <span className="font-pixel text-[9px] tracking-widest text-[var(--color-muted)] animate-pulse">
          {label}
        </span>
      )}
    </div>
  )
}

// 全屏版（替換 BusyScreen 的沙漏，或作為頁面 loading overlay）
export function PixelCatScreen({ label = 'LOADING…' }: { label?: string }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        background: 'var(--color-background)',
      }}
    >
      <PixelCatLoading size={96} label={label} />
    </div>
  )
}
